import {
  BorderRadius6,
  Box,
  Button,
  Flex,
  GreyGrey10,
  GreyGrey60,
  GreyGrey80,
  IconArrowUpRight,
  Primary13Regular,
  Primary13SemiBold,
  Skeleton
} from '@sequencehq/core-components'
import Spinner from 'components/Loading/Spinner'
import { FC, ReactNode } from 'react'
import {
  formatSalesOrderTerm,
  SalesOrderRow
} from './netsuiteSalesOrders.helpers'
import { useNetsuiteSalesOrdersWidget } from './useNetsuiteSalesOrdersWidget'
import { ProtectedButton } from 'lib/rbac'

const TEST_ID = 'netsuite.salesOrders'
const SALES_ORDERS_LABEL = 'Sales orders'

/**
 * The Sales Orders a billing schedule has synced to NetSuite. Unlike our other
 * accounting integrations, a schedule syncs per phase, so each phase maps to its
 * own Sales Order.
 */
export const NetsuiteSalesOrdersWidget: FC<{
  billingScheduleId: string
  disabled?: boolean
}> = props => {
  const hook = useNetsuiteSalesOrdersWidget(props)

  if (hook.isLoading) {
    return <Skeleton height="104px" width="100%" />
  }

  // The backend syncs a schedule in any status, so show links whenever they
  // exist and gate only the sync affordance on the schedule being active.
  if (hook.hasSalesOrders) {
    return (
      <Flex flexDirection="column" gap="8px" data-testid={TEST_ID}>
        {hook.rows.map(row => (
          <SalesOrderCard
            key={row.key}
            row={row}
            serviceName={hook.serviceName}
          />
        ))}
      </Flex>
    )
  }

  if (!hook.isActive) {
    return (
      <WidgetCard data-testid={`${TEST_ID}.notActive`}>
        <Box {...Primary13SemiBold} color={GreyGrey80}>
          {SALES_ORDERS_LABEL}
        </Box>
        <Box {...Primary13Regular} color={GreyGrey60}>
          This billing schedule will be synced once it's active.
        </Box>
      </WidgetCard>
    )
  }

  return (
    <WidgetCard data-testid={`${TEST_ID}.unsynced`}>
      <Box {...Primary13SemiBold} color={GreyGrey80}>
        {SALES_ORDERS_LABEL}
      </Box>
      <Box {...Primary13Regular} color={GreyGrey60} marginBottom="4px">
        This billing schedule has not yet been synced to {hook.serviceName}.
      </Box>
      <ProtectedButton permission={'org.billing:sync'}>
        <Button
          variant="secondary"
          onClick={hook.sync.onClick}
          disabled={hook.sync.disabled}
          data-testid={`${TEST_ID}.sync`}
        >
          {hook.sync.isSyncing ? (
            <Flex gap="8px">
              <Spinner height="16px" width="16px" color={GreyGrey60} />
              Syncing...
            </Flex>
          ) : (
            'Sync billing schedule'
          )}
        </Button>
      </ProtectedButton>
    </WidgetCard>
  )
}

const SalesOrderCard: FC<{
  row: SalesOrderRow
  serviceName: string
}> = ({ row, serviceName }) => {
  const { dateRange, duration } = formatSalesOrderTerm(row)
  const testId = `${TEST_ID}.${row.key}`

  return (
    <WidgetCard data-testid={testId}>
      <Box {...Primary13SemiBold} color={GreyGrey80}>
        {dateRange}
      </Box>
      <Box
        {...Primary13Regular}
        color={GreyGrey60}
        marginBottom="4px"
        data-testid={`${testId}.detail`}
      >
        {row.salesOrder
          ? `Sales order ${row.salesOrder.id} · ${duration}`
          : 'No sales order linked.'}
      </Box>
      {row.salesOrder?.url ? (
        <a href={row.salesOrder.url} target="_blank" rel="noreferrer">
          <Button
            variant="secondary"
            style={{ width: '100%' }}
            disabled={row.salesOrder.isPending}
            data-testid={`${testId}.view`}
          >
            <Flex alignItems="center" gap="8px">
              View in {serviceName}
              <IconArrowUpRight color={GreyGrey80} />
            </Flex>
          </Button>
        </a>
      ) : null}
    </WidgetCard>
  )
}

const WidgetCard: FC<{
  children: ReactNode
  'data-testid'?: string
}> = ({ children, 'data-testid': dataTestId }) => (
  <Flex
    flexDirection="column"
    gap="4px"
    padding="12px"
    backgroundColor={GreyGrey10}
    borderRadius={BorderRadius6}
    data-testid={dataTestId}
  >
    {children}
  </Flex>
)
